import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { CheckCircle2, XCircle, Trash2, MessageSquare, ExternalLink, Clock, Reply, X, Feather } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const TABS = [
  { key: "pending", label: "अनुमोदन बाकी", color: "amber" },
  { key: "approved", label: "अनुमोदित", color: "emerald" },
  { key: "rejected", label: "अस्वीकृत", color: "slate" },
  { key: "", label: "सभी", color: "blue" },
];

export default function CommentsPage() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySaving, setReplySaving] = useState(false);

  const load = () => {
    setLoading(true);
    const params = tab ? { status: tab } : {};
    Promise.all([
      api.get("/admin/comments", { params }),
      api.get("/admin/comments/stats"),
    ]).then(([r, s]) => {
      setItems(r.data);
      setStats(s.data);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const moderate = async (id, status) => {
    try {
      await api.patch(`/admin/comments/${id}`, { status });
      toast.success(status === "approved" ? "अनुमोदित" : "अस्वीकृत");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("इस टिप्पणी को हटा दें?")) return;
    try {
      await api.delete(`/admin/comments/${id}`);
      toast.success("हटा दी गयी");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const openReply = (c) => {
    setReplyingTo(c.id);
    setReplyText(c.editor_reply || "");
  };

  const submitReply = async () => {
    if (!replyText.trim()) { toast.error("उत्तर खाली नहीं हो सकता"); return; }
    setReplySaving(true);
    try {
      await api.post(`/admin/comments/${replyingTo}/reply`, { reply: replyText.trim() });
      toast.success("उत्तर पोस्ट हो गया");
      setReplyingTo(null);
      setReplyText("");
      load();
    } catch (e) { toast.error(formatError(e)); } finally { setReplySaving(false); }
  };

  const removeReply = async (id) => {
    if (!window.confirm("संपादक का उत्तर हटा दें?")) return;
    try {
      await api.delete(`/admin/comments/${id}/reply`);
      toast.success("उत्तर हटा दिया गया");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="admin-comments-page">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-devnagri-heading text-3xl font-black text-[#0F172A]">पाठक टिप्पणियाँ</h1>
          <p className="text-sm text-slate-500 mt-1">पाठकों की टिप्पणियों का अनुमोदन करें</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <StatChip label="कुल" value={stats.total}/>
          <StatChip label="बाकी" value={stats.pending} color="amber"/>
          <StatChip label="अनुमोदित" value={stats.approved} color="emerald"/>
          <StatChip label="अस्वीकृत" value={stats.rejected} color="slate"/>
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-slate-200 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key || "all"} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${
              tab === t.key ? "border-[#0F172A] text-[#0F172A]" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`tab-${t.key || "all"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-500 py-8 text-center">लोड हो रहा है…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 py-16 text-center bg-white rounded-lg border border-dashed border-slate-200">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 text-slate-300"/>
          इस श्रेणी में कोई टिप्पणी नहीं
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`admin-comment-${c.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-[#0F172A]">{c.name}</span>
                    {c.email && <span className="text-xs text-slate-500">{c.email}</span>}
                    <StatusBadge status={c.status}/>
                    {c.editor_reply && <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800">✎ Replied</span>}
                    <span className="text-[10px] text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3"/>{new Date(c.created_at).toLocaleString("hi-IN")}
                    </span>
                  </div>
                  <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{c.content}</div>
                  {c.news_title && (
                    <Link to={`/article/${c.news_slug}`} target="_blank" className="inline-flex items-center gap-1 text-xs text-[#1E3A8A] hover:underline mt-2">
                      <ExternalLink className="w-3 h-3"/> {c.news_title}
                    </Link>
                  )}
                  {c.editor_reply && replyingTo !== c.id && (
                    <div className="mt-3 bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white rounded-md p-3 border-l-4 border-amber-400 text-sm">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Feather className="w-3.5 h-3.5 text-amber-300"/>
                        <span className="font-black text-amber-300 text-xs tracking-wider italic" style={{ fontFamily: "'Times New Roman', serif" }}>JAKIR MOMIN</span>
                        <span className="text-[9px] uppercase tracking-widest bg-amber-400 text-[#0F172A] px-1.5 py-0.5 rounded font-bold">प्रधान संपादक</span>
                      </div>
                      <div className="whitespace-pre-wrap italic text-slate-200">{c.editor_reply}</div>
                    </div>
                  )}
                  {replyingTo === c.id && (
                    <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid={`reply-editor-${c.id}`}>
                      <div className="flex items-center gap-2 mb-2 text-xs text-amber-800 font-bold">
                        <Feather className="w-3.5 h-3.5"/> प्रधान संपादक (JAKIR MOMIN) की ओर से उत्तर
                      </div>
                      <Textarea rows={3} maxLength={2000} value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="अपना जवाब लिखें… यह उत्तर पाठक की टिप्पणी के नीचे सार्वजनिक रूप से दिखेगा।"
                        data-testid={`reply-textarea-${c.id}`}/>
                      <div className="flex justify-between items-center mt-2">
                        <div className="text-[10px] text-slate-400">{replyText.length}/2000</div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => { setReplyingTo(null); setReplyText(""); }} className="h-8">
                            <X className="w-3.5 h-3.5 mr-1"/>रद्द
                          </Button>
                          <Button size="sm" onClick={submitReply} disabled={replySaving}
                            className="bg-amber-600 hover:bg-amber-700 text-white h-8" data-testid={`reply-submit-${c.id}`}>
                            <Reply className="w-3.5 h-3.5 mr-1"/>{replySaving ? "…" : "पोस्ट करें"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap">
                  {c.status !== "approved" && (
                    <Button size="sm" onClick={() => moderate(c.id, "approved")}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" data-testid={`approve-${c.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1"/> अनुमोदित
                    </Button>
                  )}
                  {c.status !== "rejected" && (
                    <Button size="sm" variant="outline" onClick={() => moderate(c.id, "rejected")}
                      className="h-8" data-testid={`reject-${c.id}`}>
                      <XCircle className="w-3.5 h-3.5 mr-1"/> अस्वीकार
                    </Button>
                  )}
                  {c.status === "approved" && replyingTo !== c.id && (
                    <Button size="sm" onClick={() => openReply(c)} className="bg-amber-600 hover:bg-amber-700 text-white h-8" data-testid={`reply-btn-${c.id}`}>
                      <Reply className="w-3.5 h-3.5 mr-1"/>{c.editor_reply ? "उत्तर बदलें" : "उत्तर दें"}
                    </Button>
                  )}
                  {c.editor_reply && (
                    <Button size="sm" variant="outline" onClick={() => removeReply(c.id)}
                      className="h-8 text-amber-700 border-amber-200 hover:bg-amber-50" data-testid={`reply-remove-${c.id}`}>
                      <X className="w-3.5 h-3.5"/>
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => remove(c.id)}
                    className="h-8 text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-${c.id}`}>
                    <Trash2 className="w-3.5 h-3.5"/>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatChip({ label, value, color = "blue" }) {
  const map = {
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    blue: "bg-blue-50 text-blue-800 border-blue-200",
  };
  return (
    <div className={`px-3 py-1.5 rounded-md text-xs font-medium border ${map[color]}`}>
      {label}: <b className="ml-1">{value}</b>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-slate-200 text-slate-600",
  };
  const label = { pending: "बाकी", approved: "अनुमोदित", rejected: "अस्वीकृत" }[status] || status;
  return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${map[status] || "bg-slate-100"}`}>{label}</span>;
}
